// App.jsx (with serverless API proxy)
import {BrowserRouter as Router, Routes, Route} from "react-router-dom";
import LandingPage from "./components/LandingPage";
import { useState } from "react";
import { basePackingItems } from "./data/basePackingItems";
import PackingForm from "./components/PackingForm";
import PackingList from "./components/PackingList";
import ProfileManager from "./components/ProfileManager";
import logo from "./assets/logo.png"; // optional logo
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import About from "./pages/about";
import Privacy from "./pages/privacy";

export default function App() {
  const [listData, setListData] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [checkedItems, setCheckedItems] = useState({});

  const applyCounts = (items, days) => {
    const noCountItems = [
      "Sleepwear", "Shoes", "Belt",
      ...basePackingItems.climates.cold,
      ...basePackingItems.climates.hot,
      ...basePackingItems.climates.mild,
      ...basePackingItems.climates.wet
    ];
    return items.map((i) =>
      noCountItems.includes(i) ? i : `${i} x ${days}`
    );
  };

  const generateList = async ({
    destination,
    days,
    traveler,
    tripStyle,
    extras,
    customItems,
    gender,
    profileName,
    travelingWithKids,
    kidsCounts
  }) => {
    let items = {};

    if (selectedProfile) {
      traveler = selectedProfile.type;
      gender = selectedProfile.gender;
    }

    // 1) Base items
    if (basePackingItems[traveler]) {
      Object.keys(basePackingItems[traveler]).forEach((key) => {
        if (Array.isArray(basePackingItems[traveler][key])) {
          items[key] = [...(items[key] || []), ...basePackingItems[traveler][key]];
        }
      });
    }

    // 2) Gender-specific items
    const g = (gender || "").toLowerCase();
    if (traveler === "adult" && (g === "male" || g === "female")) {
      const genderPack = basePackingItems[g] || {};
      Object.keys(genderPack).forEach((cat) => {
        if (Array.isArray(genderPack[cat])) {
          items[cat] = [...(items[cat] || []), ...genderPack[cat]];
        }
      });
    }

    // 3) Trip style
    if (tripStyle && basePackingItems.tripStyles[tripStyle]) {
      items[tripStyle] = [...(items[tripStyle] || []), ...basePackingItems.tripStyles[tripStyle]];
    }

    // 4) Extras
    extras.forEach((extra) => {
      const extraPack = basePackingItems.extras[extra];
      if (!extraPack) return;

      if (typeof extraPack === "object" && (extraPack.male || extraPack.female)) {
        // ✅ gender-specific extras
        const genderKey = (gender || "").toLowerCase();
        if (extraPack[genderKey]) {
          items[extra] = [...(items[extra] || []), ...extraPack[genderKey]];
        } else if (extraPack.male) {
          // fallback if gender not set
          items[extra] = [...(items[extra] || []), ...extraPack.male];
        }
      } else if (Array.isArray(extraPack)) {
        // ✅ gender-neutral extras
        items[extra] = [...(items[extra] || []), ...extraPack];
      }
    });

    // 5) Fetch weather from proxy API
    let forecast = null;
    try {
      const res = await fetch(
        `/api/getWeather?destination=${encodeURIComponent(destination)}&days=${days}`
      );
      const data = await res.json();

      if (data.list) {
        const entries = data.list.slice(0, days * 8);
        const avgTemp = entries.reduce((sum, e) => sum + e.main.temp, 0) / entries.length;
        const conditions = entries.map((e) => e.weather[0].main.toLowerCase());
        const hasRain = conditions.some((c) => c.includes("rain"));
        const hasSnow = conditions.some((c) => c.includes("snow"));

        let climates = [];
        if (avgTemp < 40 || hasSnow) climates.push("cold");
        else if (avgTemp > 70) climates.push("hot");
        else climates.push("mild");
        if (hasRain) climates.push("wet");

        climates.forEach((cl) => {
          if (basePackingItems.climates[cl]) {
            items.clothing = [...(items.clothing || []), ...basePackingItems.climates[cl]];
          }
        });

        // Group by day
        const daily = {};
        entries.forEach((e) => {
          const localDate = new Date(e.dt * 1000).toLocaleDateString("en-US");
          if (!daily[localDate]) daily[localDate] = [];
          daily[localDate].push(e);
        });

        const today = new Date().toLocaleDateString("en-US");
        forecast = Object.keys(daily)
          .filter((date) => date >= today)
          .slice(0, 5)
          .map((date) => {
            const dayEntries = daily[date];
            const avgTemp =
              dayEntries.reduce((sum, e) => sum + e.main.temp, 0) / dayEntries.length;
            const condition = dayEntries[0].weather[0].description;
            const icon = dayEntries[0].weather[0].icon;
            return {
              date,
              avgTemp: Math.round(avgTemp),
              condition,
              icon: `https://openweathermap.org/img/wn/${icon}@2x.png`,
            };
          });
      } else if (data.error) {
        alert("Weather API error: " + data.error);
      }
    } catch (error) {
      console.error("Failed to fetch weather:", error);
    }

    // 6) Apply counts
    if (items.clothing) {
      items.clothing = applyCounts(items.clothing, days);
    }

    // 7) Custom items
    if (customItems && customItems.length > 0) {
      items.custom = [...new Set([...(items.custom || []), ...customItems])];
    }

    // 8) Kids items
    if (travelingWithKids && kidsCounts) {
      Object.entries(kidsCounts).forEach(([ageGroup, count]) => {
        if (count > 0 && basePackingItems.kids[ageGroup]) {
          const kidPack = basePackingItems.kids[ageGroup];
          if (!items.kids) items.kids = {};
          if (!items.kids[ageGroup]) items.kids[ageGroup] = [];
          Object.keys(kidPack).forEach((cat) => {
            if (Array.isArray(kidPack[cat])) {
              kidPack[cat].forEach((item) => {
                items.kids[ageGroup].push(item);
              });
            }
          });
        }
      });
    }

    setListData({
      items,
      meta: {
        destination,
        days,
        weather: forecast,
        profileName: profileName || selectedProfile?.name || "",
      },
    });
    setCheckedItems({});
    setActiveTab("list");

    setTimeout(() =>{
      window.scrollTo({top: 0, behavior: "smooth"});
    },50);
  };

  return (
    <Router>
      <Routes>
        {/* Landing page at root */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/Privacy" element={<Privacy />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Packing app at /app */}
        <Route
          path="/app"
          element={
            <div
              className="container"
              style={{
                padding: "15px 15px 70px", // keep room for nav only
                maxWidth: "480px",
                margin: "0 auto",
                minHeight: "calc(100vh - 60px)", // subtract nav height
                boxSizing: "border-box",
              }}
            >
              {/* Logo */}
              <div className="logo-wrapper">
                <img src={logo} alt="SmartPackr logo" className="logo" />
              </div>

              {/* Tabs */}
              <div >
                {activeTab === "profile" && (
                  <ProfileManager 
                    onSelectProfile={setSelectedProfile}
                    setActiveTab={setActiveTab} />
                )}
                {activeTab === "trip" && (
                  <PackingForm 
                  onGenerate={generateList} 
                  selectedProfile={selectedProfile}
                  onProfileChange={setSelectedProfile}  
                  />
                )}
                {activeTab === "list" && listData && (
                  <PackingList
                    items={listData.items}
                    meta={listData.meta}
                    checkedItems={checkedItems}
                    setCheckedItems={setCheckedItems}
                  />
                )}
              </div>

              {/* Bottom Navigation */}
              <nav className="tab-bar">
                <button
                  onClick={() => setActiveTab("profile")}
                  className={activeTab === "profile" ? "active" : ""}
                >
                  Profile
                </button>
                <button
                  onClick={() => setActiveTab("trip")}
                  className={activeTab === "trip" ? "active" : ""}
                >
                  Trip
                </button>
                <button
                  onClick={() => setActiveTab("list")}
                  className={activeTab === "list" ? "active" : ""}
                >
                  List
                </button>
              </nav>
            </div>
          }
        />
      </Routes>
    </Router>
  );
}